import React, {createContext, useContext, useEffect, useState} from "react";
import {CART_CHANGED_EVENT} from "./landing/useLandingCart";

const CartContext = createContext(undefined);

const STORAGE_KEY = "fillop_cart";

const normalizeType = (type) => String(type || "")
  .toLowerCase()
  .replace(/_/g, "-")
  .replace(/s$/, "");

const loadCart = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({children}) => {
  const [cart, setCart] = useState(loadCart);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    window.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT));
  }, [cart]);

  // Keep in sync with the landing-page cart store (same localStorage key).
  useEffect(() => {
    const handler = () => {
      const next = loadCart();
      setCart((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    window.addEventListener(CART_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CART_CHANGED_EVENT, handler);
  }, []);

  const addToCart = (item) => {
    setCart((prev) => {
      const exists = prev.some((i) => i.id === item.id && i.type === item.type);
      if (exists) return prev;
      return [...prev, item];
    });
  };

  const removeFromCart = (id, type) => {
    setCart((prev) => prev.filter((item) => !(
      String(item.id) === String(id) && normalizeType(item.type) === normalizeType(type)
    )));
  };

  const clearCart = () => setCart([]);
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  return (
    <CartContext.Provider
      value={{cart, addToCart, removeFromCart, clearCart, isCartOpen, openCart, closeCart}}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
